import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Image,
  Text,
  Spinner,
  HStack,
} from '@chakra-ui/react';
import { FiDownload, FiFileText, FiAlertCircle } from 'react-icons/fi';
import { attachmentKind, fetchAttachmentObjectUrl, downloadAttachment } from '../../services/api';

/**
 * Cover-photo-style preview for a compliance attachment.
 *
 * Attachments are auth-gated, so we can't point <img>/<iframe> at the raw URL —
 * we fetch the bytes through axios (token attached) and render from an object
 * URL that we own and revoke on close. Images render in an <img>, PDFs in an
 * <iframe> (browser-native viewer), and PowerPoint falls back to a download
 * prompt since browsers can't render decks inline.
 */
const EventAttachmentPreviewModal = ({ isOpen, attachment, onClose }) => {
  const [state, setState] = useState({ status: 'idle', objectUrl: '', contentType: '' });
  // Track the live object URL in a ref so cleanup always revokes the latest one
  // regardless of re-renders.
  const objectUrlRef = useRef('');

  const kind = attachment ? attachmentKind(attachment.contentType, attachment.name) : 'other';
  const canPreview = kind === 'image' || kind === 'pdf';

  useEffect(() => {
    if (!isOpen || !attachment?.url) return undefined;

    let cancelled = false;

    const revoke = () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
      }
    };

    // PowerPoint (and anything not previewable) never fetches — we show the
    // download fallback immediately.
    if (!canPreview) {
      setState({ status: 'unsupported', objectUrl: '', contentType: '' });
      return revoke;
    }

    setState({ status: 'loading', objectUrl: '', contentType: '' });
    (async () => {
      try {
        const { objectUrl, contentType } = await fetchAttachmentObjectUrl(attachment.url);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        objectUrlRef.current = objectUrl;
        setState({ status: 'ready', objectUrl, contentType });
      } catch (err) {
        if (!cancelled) setState({ status: 'error', objectUrl: '', contentType: '' });
      }
    })();

    return () => {
      cancelled = true;
      revoke();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, attachment?.url, canPreview]);

  const handleDownload = () => {
    if (attachment?.url) downloadAttachment(attachment.url, attachment.name);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="rgba(0,0,0,0.7)" backdropFilter="blur(2px)" />
      <ModalContent
        bg="#0f0f10"
        color="#fafafa"
        border="1px solid rgba(255,255,255,0.1)"
        borderRadius="16px"
        maxH="88vh"
      >
        <ModalHeader fontSize="md" fontWeight="700" pr={12}>
          <HStack spacing={2} align="center">
            <Box color="rgba(255,255,255,0.55)" flexShrink={0} display="flex" alignItems="center">
              <FiFileText size={15} />
            </Box>
            <Text noOfLines={1}>{attachment?.name || 'Attachment'}</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton color="rgba(255,255,255,0.6)" />

        <ModalBody pb={2}>
          <Box
            borderRadius="12px"
            overflow="hidden"
            bg="#000"
            border="1px solid rgba(255,255,255,0.08)"
            minH="60vh"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {state.status === 'loading' && (
              <Flex direction="column" align="center" gap={3} py={10}>
                <Spinner size="md" color="rgba(255,255,255,0.7)" thickness="2px" />
                <Text fontSize="sm" color="rgba(255,255,255,0.5)">Loading preview…</Text>
              </Flex>
            )}

            {state.status === 'ready' && kind === 'image' && (
              <Image
                src={state.objectUrl}
                alt={attachment?.name || 'Attachment'}
                maxW="100%"
                maxH="78vh"
                objectFit="contain"
              />
            )}

            {state.status === 'ready' && kind === 'pdf' && (
              <Box
                as="iframe"
                title={attachment?.name || 'PDF preview'}
                src={state.objectUrl}
                w="100%"
                h="78vh"
                border="none"
                bg="#fff"
              />
            )}

            {state.status === 'unsupported' && (
              <Flex direction="column" align="center" gap={3} py={12} px={6} textAlign="center">
                <Box color="rgba(255,255,255,0.4)" display="flex" alignItems="center">
                  <FiFileText size={34} />
                </Box>
                <Text fontSize="sm" fontWeight="600" color="#fafafa">
                  Preview isn’t available for this file type
                </Text>
                <Text fontSize="xs" color="rgba(255,255,255,0.45)" maxW="360px" lineHeight="1.7">
                  PowerPoint files can’t be displayed in the browser. Download the file to open it in
                  PowerPoint, Keynote, or Google Slides.
                </Text>
                <Button
                  leftIcon={<FiDownload size={14} />}
                  size="sm"
                  mt={1}
                  borderRadius="full"
                  bg="rgba(255,255,255,0.92)"
                  color="#0a0a0a"
                  _hover={{ bg: '#fff' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={handleDownload}
                >
                  Download file
                </Button>
              </Flex>
            )}

            {state.status === 'error' && (
              <Flex direction="column" align="center" gap={3} py={12} px={6} textAlign="center">
                <Box color="rgba(248,113,113,0.7)" display="flex" alignItems="center">
                  <FiAlertCircle size={30} />
                </Box>
                <Text fontSize="sm" fontWeight="600" color="#fafafa">
                  Couldn’t load this file
                </Text>
                <Text fontSize="xs" color="rgba(255,255,255,0.45)" maxW="360px" lineHeight="1.7">
                  The file may have been removed, or your session may need a refresh. You can still
                  try downloading it.
                </Text>
                <Button
                  leftIcon={<FiDownload size={14} />}
                  size="sm"
                  mt={1}
                  variant="outline"
                  borderRadius="full"
                  color="#fafafa"
                  borderColor="rgba(255,255,255,0.2)"
                  _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={handleDownload}
                >
                  Download file
                </Button>
              </Flex>
            )}
          </Box>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            size="sm"
            color="rgba(255,255,255,0.7)"
            _hover={{ bg: 'rgba(255,255,255,0.06)' }}
            _focus={{ boxShadow: 'none' }}
            onClick={onClose}
          >
            Close
          </Button>
          <Button
            leftIcon={<FiDownload size={14} />}
            size="sm"
            color="#fafafa"
            borderRadius="full"
            border="1px solid rgba(255,255,255,0.12)"
            variant="ghost"
            _hover={{ bg: 'rgba(255,255,255,0.06)' }}
            _focus={{ boxShadow: 'none' }}
            onClick={handleDownload}
          >
            Download
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EventAttachmentPreviewModal;
